import { useEffect, type ReactNode } from "react";
import {
  animate,
  useMotionValue,
  useReducedMotion,
  useTransform,
  motion,
} from "framer-motion";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

export type BurnStatTone = "safe" | "warn" | "over" | "neutral";

export interface BurnStatWidgetProps {
  icon: ReactNode;
  label: string;
  value: number;
  /** Formatter for the ticker number. */
  format?: (n: number) => string;
  sublabel?: string;
  tone?: BurnStatTone;
  /** Optional trend value in percent. Positive = up chevron, negative = down. */
  trend?: number;
  /** Pulse the accent ring (e.g. critical threshold crossed). */
  pulse?: boolean;
  className?: string;
}

const TONE_ACCENT: Record<BurnStatTone, string> = {
  safe: "var(--color-brand-1)",
  warn: "#fbbf24",
  over: "#ef4444",
  neutral: "#94a3b8",
};

/** Single animated battle-stat tile. Counts up from 0 on mount over ~800ms,
 * glass panel with a radial accent glow behind the icon. */
export function BurnStatWidget({
  icon,
  label,
  value,
  format = (n) => Math.round(n).toLocaleString("es-ES"),
  sublabel,
  tone = "neutral",
  trend,
  pulse = false,
  className,
}: BurnStatWidgetProps) {
  const reduced = useReducedMotion();
  const mv = useMotionValue(reduced ? value : 0);
  const text = useTransform(mv, (v) => format(v));

  useEffect(() => {
    if (reduced) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1], // ease-out
    });
    return () => controls.stop();
  }, [value, mv, reduced]);

  const accent = TONE_ACCENT[tone];
  const trendUp = trend !== undefined && trend >= 0;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[16px] border border-border-1 bg-white/[0.03] px-5 py-4 backdrop-blur-sm",
        className,
      )}
      style={{
        boxShadow: pulse
          ? `0 0 0 1px ${accent}33, 0 0 24px ${accent}26`
          : undefined,
      }}
    >
      {/* radial glow behind the icon */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-4 -top-6 h-28 w-28 rounded-full opacity-60 blur-2xl"
        style={{ background: `radial-gradient(circle, ${accent}33 0%, transparent 70%)` }}
      />

      <div className="relative flex items-start justify-between gap-3">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-[10px] border border-border-1 bg-white/[0.04]",
            pulse && "animate-pulse",
          )}
          style={{ color: accent, boxShadow: `inset 0 0 12px ${accent}22` }}
        >
          {icon}
        </div>
        {trend !== undefined && (
          <div
            className="flex items-center gap-0.5 rounded-full border border-border-1 bg-white/[0.03] px-2 py-0.5 text-[10px] font-semibold"
            style={{ color: trendUp ? "#10b981" : "#ef4444" }}
          >
            {trendUp ? (
              <ChevronUp className="h-3 w-3" strokeWidth={2.5} />
            ) : (
              <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
            )}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>

      <div className="relative mt-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-text-4">
          {label}
        </div>
        <motion.div
          className="tabular mt-1 text-[28px] leading-none font-bold font-display text-text-1"
          style={{ color: tone === "neutral" ? undefined : accent }}
        >
          {text}
        </motion.div>
        {sublabel && (
          <div className="mt-1 text-xs text-text-3">{sublabel}</div>
        )}
      </div>
    </div>
  );
}
